/**
 * Hook wrapper around the Stage-editor reactive variable (SE-5488).
 *
 * Exposes the current editor state plus imperative open/close helpers so any
 * surface can drive the editor without touching the raw `makeVar`. Mirrors
 * `useRulesPanelState`.
 * @module features/player-kanban/hooks/useStageEditorPanelState
 */

import { useReactiveVar } from "@apollo/client";
import { useCallback, useMemo } from "react";

import {
  resetStageEditor,
  type StageEditorState,
  stageEditorVar,
} from "../stores/stageEditorStore";

/** Public API returned by {@link useStageEditorPanelState}. */
export interface UseStageEditorPanelStateResult {
  /** Current editor state. */
  readonly state: StageEditorState;
  /**
   * Open the editor scoped to one pipeline board.
   * @param kanbanBoardId - The pipeline whose stages to edit.
   */
  readonly open: (kanbanBoardId: string) => void;
  /** Close the editor. */
  readonly close: () => void;
}

/**
 * Subscribe to the Stage-editor reactive variable + open/close helpers.
 * @returns The {@link UseStageEditorPanelStateResult}.
 */
export function useStageEditorPanelState(): UseStageEditorPanelStateResult {
  const state = useReactiveVar(stageEditorVar);

  const open = useCallback(
    (kanbanBoardId: string) => stageEditorVar({ open: true, kanbanBoardId }),
    []
  );

  const close = useCallback(() => resetStageEditor(), []);

  return useMemo(() => ({ state, open, close }), [state, open, close]);
}
